import React, { useState, useEffect } from 'react';
import { RotateCcw } from 'react-feather';
import { getRefundData, approveRequestRefund, rejectRequestRefund } from '../../../Api/apiUtils';
import { useSearchParams } from 'react-router-dom';
import './refundStyle.scss';
import { toast } from 'react-toastify';
import { formatDate } from '../../../utils/formatDate';
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  SearchInput,
  Select,
  Toolbar,
  StatusBadge,
  Badge,
  Table,
  DescriptionList,
  Disclosure,
  Loader,
  EmptyState,
  TextInput,
  useConfirm,
} from '../../../ui';

interface Course {
  courseId: string;
  courseName: string;
  isFree: boolean;
  unit_price: number;
  classesToPurchase: number;
  discountedPrice: number;
  price: number;
  eligibleRefundClasses: number;
  requestedRefundClasses: number;
  refundableAmount: number;
  refundDiscout: number;
  approvel_classes?: number;
  approvel_amount?: number;
  approvel_discount?: number;
}

interface Transaction {
  id: string;
  name: string;
  countryCode: string;
  contact: string;
  createdAt: string;
  refundCreatedAt: string;
  captureDate: string;
  currency: string;
  totalPrice: number;
  courseList: Course[];
  reasonforrefund?: string;
  refund_status?: string;
}

const Refund: React.FC = () => {
  const [refundRequestList, setRefundRequestList] = useState<any>([]);

  // Toolbar (same pattern as Referral Management): search + status + sort.
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
  ];
  const sortOptions = [
    { value: 'desc', label: 'Newest first' },
    { value: 'asc', label: 'Oldest first' },
  ];
  const { confirm, dialog } = useConfirm();

  // Rows keep their ORIGINAL index — handleChange/approve mutate
  // refundRequestList by index, so filtering must never renumber them.
  const visibleRows: { v: any; i: number }[] = refundRequestList
    .map((v: any, i: number) => ({ v, i }))
    .filter(({ v }: { v: any }) => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !term ||
        String(v.name || '').toLowerCase().includes(term) ||
        String(v.contact || '').includes(term);
      const isPending = v.refund_status === 'pending';
      const matchesStatus =
        !statusFilter || (statusFilter === 'pending' ? isPending : !isPending);
      return matchesSearch && matchesStatus;
    })
    .sort((a: { v: any }, b: { v: any }) => {
      const ta = new Date(a.v.refundCreatedAt).getTime() || 0;
      const tb = new Date(b.v.refundCreatedAt).getTime() || 0;
      return sortOrder === 'asc' ? ta - tb : tb - ta;
    });
  const [loading, setLoading] = useState<boolean>(false);
  const [searchParam] = useSearchParams();
  const [openIndex, setOpenIndex] = useState<number | null>(null); 

  useEffect(() => {
    fetchRefundData();
  }, []);

  const fetchRefundData = async () => {
    setLoading(true);
    try {
      const response = await getRefundData();
 
  
      if (!response.success) {
        toast.error(response.message || 'Failed to fetch refund data.');
        return;
      }
  
      if (!response.data?.Items || response.data.Items.length === 0) {
        console.error('No items found in the API response.');
        toast.error('No refund requests found.');
        return;
      }

      const sortedData = response.data.Items.sort((a: Transaction, b: Transaction) => {
        const dateA = new Date(a.refundCreatedAt).getTime();
        const dateB = new Date(b.refundCreatedAt).getTime();
        return dateB - dateA; // Newest first
      }).sort((a: Transaction, b: Transaction) => {
        const statusA = a.refund_status?.toLowerCase();
        const statusB = b.refund_status?.toLowerCase();
        if (statusA === 'pending' && statusB !== 'pending') return -1; 
        if (statusA !== 'pending' && statusB === 'pending') return 1;
        return 0;
      });
  
  
      setRefundRequestList(sortedData);
    } catch (error) {
      toast.error('An error occurred while fetching refund data.');
    } finally {
      setLoading(false);
    }
  };


  const toggleCollapse = (index: number) => {
    setOpenIndex(openIndex === index ? null : index); // Toggle open/close
  };
  // const formatDate = (dateString: string, includeTime: boolean = false): string => {
  //   const date = new Date(dateString);
  //   return includeTime ? date.toLocaleString() : date.toLocaleDateString();
  // };

  const handleChange = (index: number, idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedList = [...refundRequestList];

    updatedList[index].courseList[idx] = {
      ...updatedList[index].courseList[idx],
      [name]: value,
    };

    updatedList[index].courseList[idx].approvel_amount =
      ((updatedList[index].courseList[idx].approvel_classes || 0) * updatedList[index].courseList[idx].unit_price) -
      (updatedList[index].courseList[idx].approvel_discount || 0);

    setRefundRequestList(updatedList);
  };
  const approve = async (v: any) => {
    const ok = await confirm({
      title: 'Approve refund?',
      message: `The refund request for ${v.name} will be approved for the classes and amounts entered.`,
      confirmLabel: 'Approve refund',
    });
    if (!ok) return;
    setLoading(true);
    try {
      let dt = new Date();
      let d = dt.getDate();
      let m = dt.getMonth() + 1;
      let y = dt.getFullYear();
      v.token = localStorage.getItem('authToken') || '';
      v.refund_status = "approved";
      v.refundDate = (y + '-' + (m.toString().length < 2 ? '0' + m : m) + '-' + (d.toString().length < 2 ? '0' + d : d));
      v.courseList.forEach((e:any) => {
        if(e.isFree){
           e.approvel_amount = 0;
           e.approvel_classes = +e.classesToPurchase;
           e.approvel_discount = +e.approvel_discount || 0;
        }
      });
      // const courseList = transaction.courseList.map((course) => ({
      //   courseId: course.courseId,
      //   approvel_classes: Number(course.approvel_classes) || 0,
      //   approvel_amount: Number(course.approvel_amount) || 0,
      //   approvel_discount: Number(course.approvel_discount) || 0,
      // }));
      // const payload = {
      //   id: transaction.id,
      //   refund_status: "approved",
      //   refundDate: new Date().toISOString().split('T')[0], 
      //   courseList, 
      // };
  
      // console.log("✅ Sending Payload:", JSON.stringify(payload, null, 2)); 
  
      const response = await approveRequestRefund(v);
      
      console.log("🔄 API Response:", response.data); 
  
      if (response.status === 200) {
        toast.success(`Refund request for ${v.name} approved.`);
        fetchRefundData(); 
        setOpenIndex(null); // Close the collapse after removing
      } else {
        toast.error(response.data.message || 'Failed to approve refund request.');
      }
    } catch (error: any) {
     
      toast.error('An error occurred while approving refund request.');
    } finally {
      setLoading(false);
    }
  };
  const reject = async (v: any) => {
    const ok = await confirm({
      title: 'Remove refund request?',
      message: `The refund request for ${v.name} will be removed. This can't be undone.`,
      confirmLabel: 'Remove request',
      tone: 'danger',
    });
    if (!ok) return;
    setLoading(true);
    try {
      const response = await rejectRequestRefund(v);
      console.log("🔄 API Response:", response.data); 
      if (response.status === 200) {
        toast.success(`Refund request for ${v.name} Removed.`);
        fetchRefundData(); 
        setOpenIndex(null); // Close the collapse after removing
      } else {
        toast.error(response.data.message || 'Failed to remove refund request.');
      }
    } catch (error: any) {
     
      toast.error('An error occurred while removing refund request.');
    } finally {
      setLoading(false);
    }
  }

  
  const refundTotal = (v: any) =>
    +(v.courseList || []).reduce((acc: number, course: any) => acc + (course.approvel_amount || 0), 0).toFixed(2);
  const money = (currency: string, amount: number | string | undefined) => `${currency} ${amount ?? 0}`;
  const pendingCount = refundRequestList.filter((v: any) => v.refund_status === 'pending').length;

  return (
    <Page>
      <PageHeader
        eyebrow="Consumer Xperience"
        title="Refund Management"
        subtitle={
          loading
            ? 'Loading refund requests…'
            : `${refundRequestList.length} requests · ${pendingCount} pending review`
        }
        actions={
          <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={fetchRefundData} disabled={loading}>
            Refresh
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <Field label="Search" className="ds-toolbar__grow">
            {(ctl) => (
              <SearchInput
                {...ctl}
                placeholder="Name or contact number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
          </Field>
          <Field label="Status">
            {(ctl) => (
              <Select {...ctl} options={statusOptions} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
            )}
          </Field>
          <Field label="Sort by request date">
            {(ctl) => (
              <Select
                {...ctl}
                options={sortOptions}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              />
            )}
          </Field>
        </Toolbar>
      </Card>

      {loading ? (
        <Loader label="Loading refund requests…" />
      ) : visibleRows.length === 0 ? (
        <EmptyState
          title="No refund requests match"
          description="Try a different name or contact number, or set Status to All Status."
        />
      ) : (
        <div className="ds-stack">
          {visibleRows.map(({ v, i: index }: { v: any; i: number }) => {
            const isPending = v.refund_status === 'pending';
            return (
              <Disclosure
                key={index}
                open={openIndex === index}
                onToggle={() => toggleCollapse(index)}
                summary={
                  <div className="refund-row">
                    <div className="refund-row__who">
                      <span className="ds-strong">{v.name}</span>
                      <span className="ds-text-subtle">Requested {formatDate(v.refundCreatedAt)}</span>
                    </div>
                    <div className="refund-row__amounts">
                      <span className="refund-row__amount">
                        <span className="ds-text-subtle">Purchased</span>
                        <span className="ds-num">{money(v.currency, v.totalPrice)}</span>
                      </span>
                      <span className="refund-row__amount">
                        <span className="ds-text-subtle">Refund</span>
                        <span className="ds-num ds-strong">{money(v.currency, refundTotal(v))}</span>
                      </span>
                    </div>
                    <StatusBadge status={isPending ? 'pending' : 'approved'} />
                  </div>
                }
              >
                <DescriptionList
                  items={[
                    { label: 'Phone', value: `${v.countryCode} ${v.contact}` },
                    { label: 'Transaction date', value: formatDate(v.createdAt) },
                    { label: 'Capture date', value: formatDate(v.captureDate, 'shortDate') },
                    { label: 'Reason for refund', value: v.reasonforrefund || '—' },
                  ]}
                />

                <Table caption={`Courses in the refund request for ${v.name}`}>
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th className="ds-num">Price / class (inc. tax)</th>
                      <th className="ds-num">Classes</th>
                      <th className="ds-num">Discount</th>
                      <th className="ds-num">Total price</th>
                      <th className="ds-num">Refundable classes</th>
                      <th className="ds-num">Refundable amount ({v.currency})</th>
                      <th className="ds-num">Discount on refund ({v.currency})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(v.courseList || []).map((z: any, idx: number) => (
                      <tr key={idx}>
                        <td>
                          {z.courseName} {z.isFree && <Badge tone="success">Free course</Badge>}
                        </td>
                        <td className="ds-num">{z.unit_price}</td>
                        <td className="ds-num">{z.classesToPurchase}</td>
                        <td className="ds-num">{z.discountedPrice}</td>
                        <td className="ds-num">{z.price}</td>
                        <td>
                          <TextInput
                            size="sm"
                            numeric
                            name="approvel_classes"
                            aria-label={`Refundable classes for ${z.courseName}`}
                            disabled={z.isFree || !isPending}
                            value={z.approvel_classes ?? ''}
                            onChange={(e) => handleChange(index, idx, e)}
                          />
                        </td>
                        <td>
                          <TextInput
                            size="sm"
                            numeric
                            name="approvel_amount"
                            aria-label={`Refundable amount for ${z.courseName}`}
                            readOnly
                            value={z.approvel_amount ?? ''}
                          />
                        </td>
                        <td>
                          <TextInput
                            size="sm"
                            numeric
                            name="approvel_discount"
                            aria-label={`Discount on refund for ${z.courseName}`}
                            disabled={z.isFree || !isPending}
                            value={z.approvel_discount ?? ''}
                            onChange={(e) => handleChange(index, idx, e)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={6}>Total refund</td>
                      <td className="ds-num">{money(v.currency, refundTotal(v))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </Table>

                {isPending && (
                  <div className="refund-actions">
                    <Button variant="ghost" className="refund-actions__remove" onClick={() => reject(v)}>
                      Remove request
                    </Button>
                    <Button variant="primary" onClick={() => approve(v)}>
                      Approve refund
                    </Button>
                  </div>
                )}
              </Disclosure>
            );
          })}
        </div>
      )}
      {dialog}
    </Page>
  );
};

export default Refund;